<template>
  <main class="support">
    <div class="container">
      <!-- Hero -->
      <section class="hero">
        <div class="hero__content">
          <h1>Support</h1>
          <p class="sub">
            Answers to common questions and a direct line to the PathTracer team.
          </p>
        </div>
        <div class="hero__art" aria-hidden="true"></div>
      </section>

      <!-- FAQ -->
     <section class="card">
  <h2 class="section-title">Frequently Asked Questions</h2>
  <div class="faqs">
    <details
      v-for="faq in faqs"
      :key="faq.q"
      class="faq"
    >
      <summary
        data-track="support_faq_toggle"
        :data-track-props="JSON.stringify({ q: faq.q })"
      >
        {{ faq.q }}
      </summary>
      <p>{{ faq.a }}</p>
    </details>
  </div>
</section>

      <!-- SLA / notice -->
      <p v-if="hasSentMessage" class="response-notice" role="status" aria-live="polite">
        Support will review your message as soon as possible. Please allow up to
        <strong>3 business days</strong> for a reply.
      </p>

      <!-- Chat -->
      <section class="card card--chat">
        <div class="card__header">
          <h2>Message Support</h2>
        <span
  v-if="statusMessage"
  class="status-banner"
  :class="statusType"
  data-track="support_status_banner_click"
  :data-track-props="JSON.stringify({ type: statusType })"
>
  {{ statusMessage }}
</span>
        </div>

        <div class="chat">
          <div v-if="isLoadingChat" class="loading-chat">Loading chat…</div>

          <div v-else class="chat__log" ref="chatLog" 
  data-track="support_chat_interact"
  :data-track-props="JSON.stringify({ action:'click_log' })">
            <div
              v-for="msg in chatHistory"
              :key="msg.id"
              :class="[
                'msg',
                msg.username === username ? 'is-self' :
                (msg.role === 'admin' || msg.role === 'moderator') ? 'is-support' : 'is-user'
              ]"
            >
              <div class="avatar" :data-initial="(msg.role === 'admin' || msg.role === 'moderator') ? 'PT' : (msg.username || '?')[0]?.toUpperCase()"></div>

              <div class="bubble">
                <div class="meta">
                  <span class="who">
                    {{
                      (msg.role === 'admin' || msg.role === 'moderator')
                        ? 'PathTracer Support'
                        : (msg.username || 'User')
                    }}
                  </span>
                  <span class="time">{{ formatTime(msg.timestamp) }}</span>
                </div>
                <div class="text">{{ msg.message }}</div>
              </div>
            </div>
          </div>

          <!-- Composer -->
          <form class="composer" @submit.prevent="sendMessage">
            <textarea
              v-model="newMessage"
              placeholder="Type your message…"
              required
              maxlength="3000"
              :disabled="isSending"
              @input="autoGrow"
              ref="composer"
            />
           <div class="composer__actions">
    <button
      type="submit"
      :disabled="isSending || !newMessage.trim()"
      class="btn btn--primary"
      data-track="support_send_click"
      :data-track-props="JSON.stringify({
        len: (newMessage || '').length
      })"
    >
      <span v-if="isSending" class="spinner"></span>
      {{ isSending ? 'Sending…' : 'Send' }}
    </button>
  </div>
            <input type="text" v-model="importantInfo" autocomplete="off" class="importantInfo" />
          </form>
        </div>
      </section>
    </div>
  </main>
</template>

<script>
import axios from 'axios';

export default {
  name: 'SupportPage',
  data() {
    return {
      hasSentMessage: false,
      isLoadingChat: true,
      importantInfo: '',
      statusMessage: '',
      statusType: '', // 'success' | 'error'
      faqs: [
        { q: "My image didn't show up but it used a credit.", a: "Re-run it, then press Report Bug beside the transaction. We'll refund the credit after admin review." },
        { q: "How do I get Advanced Mode / what does it do?", a: "Advanced mode is in beta. It includes visual overlays and a spreadsheet of all paths, hashes, and amounts followed. Contact support@pathtracer.io for pricing." },
        { q: "My image turned out blurry.", a: "Some complex paths reduce image quality. You can message us to request a higher resolution version." },
        { q: "How can I submit a bug report?", a: "Click Report Bug next to the transaction." },
        { q: "My transaction is taking longer than 10 minutes.", a: "It may be stuck in the queue. Please be patient—an admin will resolve it shortly." },
        { q: "What does a referral code do?", a: "Nothing quite yet but we'll be looking into promotional bonuses for referral codes." }
      ],
      chatHistory: [],
      newMessage: '',
      isSending: false,
      username: localStorage.getItem('username') || '',
      role: localStorage.getItem('role') || '',
      refreshIntervalId: null
    };
  },
mounted() {
  this.fetchChatHistory();
  this.refreshIntervalId = setInterval(this.fetchChatHistory, 15000);
  document.body.classList.add('route-support');     // <--
},
beforeUnmount() {
  if (this.refreshIntervalId) clearInterval(this.refreshIntervalId);
  document.body.classList.remove('route-support');  // <--
},

  methods: {
    autoGrow(e) {
      const el = e?.target || this.$refs.composer;
      if (!el) return;
      el.style.height = 'auto';
      el.style.height = Math.min(el.scrollHeight, 200) + 'px';
    },
    showStatus(msg, type = 'success', duration = 3000) {
      this.statusMessage = msg;
      this.statusType = type;
      setTimeout(() => { this.statusMessage = ''; this.statusType = ''; }, duration);
    },
formatTime(iso) {
  const d = new Date(iso);
  if (isNaN(d)) return '';

  const now = new Date();
  const isToday = d.toDateString() === now.toDateString();
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  const isYesterday = d.toDateString() === yesterday.toDateString();

  const timeStr = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  if (isToday) return `Today ${timeStr}`;
  if (isYesterday) return `Yesterday ${timeStr}`;
  return d.toLocaleString();
}
,
    fetchChatHistory() {
      axios.get('/etherscan/fetch_support_chat.php', {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      }).then(res => {
        if (res.data.status === 'success') {
          const log = this.$refs.chatLog;
          const atBottom = log && (log.scrollTop + log.clientHeight >= log.scrollHeight - 5);
          const prevLen = this.chatHistory.length;

          this.chatHistory = res.data.messages;

          this.$nextTick(() => {
            // Auto-scroll if new messages or the user was already at the bottom
            if (log && (this.chatHistory.length !== prevLen || atBottom)) {
              log.scrollTop = log.scrollHeight;
            }
          });
        }
      }).finally(() => {
        this.isLoadingChat = false;
      });
    },
    sendMessage() {
      if (!this.newMessage.trim()) return;
      this.isSending = true;

      axios.post('/etherscan/send_support_message.php',
        { message: this.newMessage, importantInfo: this.importantInfo },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      ).then(res => {
        if (res.data.status === 'success') {
          this.newMessage = '';
          this.hasSentMessage = true;
          this.fetchChatHistory();
          this.showStatus('Message sent.', 'success');
          this.$nextTick(() => this.autoGrow()); // reset textarea height
        } else {
          this.showStatus('Failed to send message.', 'error');
        }
      }).catch(() => {
        this.showStatus('Network error. Try again.', 'error');
      }).finally(() => {
        this.isSending = false;
      });
    }
  }
};
</script>

<style scoped>
/* Scope matches the rest of your new light pages (no global bg here to avoid “double background”) */
.support {
  --card: #ffffff;
  --ink: #1f2a37;
  --muted: #6b7280;
  --border: #e5e8ec;
  --accent: #35495e;
  --accent-2: #2c80b4;

  color: var(--ink);
  padding: 1.25rem 1rem 2.5rem;
  background: transparent; /* important: no second background layer */
}
.container { max-width: 1120px; margin: 0 auto; }

/* HERO */
.hero {
  position: relative;
  overflow: hidden;
  border-radius: 16px;
  background: linear-gradient(180deg, #ffffff, #f3f6fb);
  border: 1px solid var(--border);
  padding: clamp(1.25rem, 3vw, 2rem);
}
.hero__content { max-width: 920px; margin: 0 auto; text-align: left; }
.hero h1 { margin: 0; font-size: clamp(1.6rem, 1.8rem + 1vw, 2.2rem); line-height: 1.15; }
.sub { margin: .5rem 0 0; color: var(--muted); max-width: 60ch; }
.hero__art {
  position: absolute; inset: -20% -10% auto auto; height: 240px; width: 480px;
  background:
    radial-gradient(240px 150px at 70% 30%, rgba(44,128,180,.16), transparent 60%),
    radial-gradient(200px 130px at 30% 70%, rgba(53,73,94,.12), transparent 60%);
  pointer-events: none;
}

/* Cards */
.card {
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: 14px;
  box-shadow: 0 10px 20px rgba(16,24,40,.06);
  padding: 1.2rem;
  margin-top: 1rem;
}

/* FAQ */
.section-title { margin: 0; font-size: 1.15rem; }
.faqs { margin-top: .6rem; display: grid; gap: .4rem; }
.faq {
  border: 1px solid var(--border);
  border-radius: 10px;
  background: #fff;
  padding: .6rem .8rem;
}
.faq summary {
  cursor: pointer;
  list-style: none;
  font-weight: 600;
  position: relative;
}
.faq summary::-webkit-details-marker { display: none; }
.faq summary::after {
  content: "›";
  transform: rotate(90deg);
  position: absolute;
  right: 0;
  top: 0;
  color: var(--muted);
}
.faq[open] summary::after { transform: rotate(-90deg); }
.faq p { margin: .5rem 0 0; color: var(--muted); }

/* Notice / SLA */
.response-notice {
  font-size: 0.95rem;
  color: #5a3b00;
  margin: .75rem 0 0;
  background-color: #fff7e6;
  padding: 8px 12px;
  border-left: 4px solid #ffa726;
  border-radius: 6px;
}

/* Chat card */
.card--chat .card__header {
  display: flex; align-items: center; justify-content: space-between; gap: .5rem; margin-bottom: .5rem;
}
.status-banner {
  padding: 6px 10px; border-radius: 999px; font-weight: 700; font-size: .85rem;
  border: 1px solid var(--border);
}
.status-banner.success { background: #e9f7ef; color: #20744a; border-color: #ccebd9; }
.status-banner.error { background: #fdeaea; color: #8f1d1d; border-color: #f4c7c7; }

/* Chat layout */
.chat { display: grid; gap: .6rem; }
/* 1) Keep last messages visible behind sticky composer */
.chat__log {
  /* was: max-height: 360px; overflow-y: auto; padding: .25rem; */
  padding: .25rem;
  padding-bottom: 96px; /* space for sticky composer (approx button+gap) */
}
.loading-chat { text-align: center; padding: 1em; color: #666; font-style: italic; }

.msg {
  display: grid; grid-template-columns: auto 1fr; gap: .6rem; align-items: start;
  margin-bottom: .35rem;
}
.is-self { grid-template-columns: 1fr auto; }
.is-self .avatar { order: 2; }
.is-self .bubble { order: 1; justify-self: end; }
.is-support .avatar { background: #c8e6c9; color: #1b5e20; }
.is-support .bubble { background: #e9f7ef; border-color: #d6efe2; }
.is-user   .bubble { background: #f7f9fc; border-color: #e6edf5; }
.is-self   .bubble { background: #dbeeff; border-color: #c7e2ff; }

/* Avatar as initial(s) */
.avatar {
  --size: 34px;
  width: var(--size); height: var(--size);
  border-radius: 50%; background: #e5e8ec; color: #374151;
  display: grid; place-items: center; font-weight: 800; font-size: .8rem;
}
.avatar::after { content: attr(data-initial); }

/* Bubble */
.bubble {
  max-width: min(78ch, 90%);
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: .6rem .7rem;
  box-shadow: 0 1px 2px rgba(16,24,40,.06);
}
.meta {
  display: flex; gap: .5rem; justify-content: space-between; align-items: baseline;
  margin-bottom: .25rem; font-size: .78rem; color: var(--muted);
}
.who { font-weight: 700; color: #374151; }
.time { white-space: nowrap; }
.text { white-space: pre-wrap; word-wrap: break-word; }

/* 2) More breathing room for composer + align the button baseline */
/* Let the textarea actually shrink in a grid */
.composer { 
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto; /* <-- minmax(0,1fr) is key */
  column-gap: .8rem;
  row-gap: .6rem;
  align-items: end;
  position: sticky;
  bottom: 0;
  background: #fff;
  padding-top: .6rem;
  padding-bottom: .4rem;
  border-top: 1px solid var(--border);
}

/* Prevent overflow and make width calc sane */
.composer textarea {
  min-width: 0;               /* <-- allow shrinking */
  box-sizing: border-box;     /* <-- width includes padding/border */
  width: 100%;
  min-height: 64px;
  max-height: 200px;
  resize: none;
  padding: .8rem .9rem;
  border: 1px solid var(--border);
  border-radius: 10px;
  font: inherit;
  color: var(--ink);
}
/* Also ensure the actions column never pushes into the textarea */
.composer__actions { min-width: 0; }
.composer__actions .btn { min-width: 120px; height: 44px; }


.btn {
  display: inline-flex; align-items: center; justify-content: center; gap: 6px;
  padding: .75rem 1rem; border-radius: 10px; font-weight: 700; border: none; cursor: pointer;
  transition: filter 140ms ease, box-shadow 140ms ease;
}
.btn--primary { background: linear-gradient(180deg, var(--accent-2), #1f6d9a); color: #fff; box-shadow: 0 6px 14px rgba(31,109,154,.18); }
.btn--primary:hover:not(:disabled) { filter: brightness(1.05); }
.btn:disabled { opacity: .6; cursor: not-allowed; }

.spinner {
  width: 18px; height: 18px; border: 3px solid rgba(255,255,255,.35); border-top: 3px solid #fff;
  border-radius: 50%; animation: spin .6s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }


.importantInfo { position: absolute; left: -9999px; top: auto; width: 1px; height: 1px; overflow: hidden; }

/* Small screens */
@media (max-width: 560px) {
  .chat__log { max-height: 420px; }
  .bubble { max-width: 100%; }
}

/* 3) On smaller screens, stack the button under the textarea */
@media (max-width: 720px) {
  .composer {
    grid-template-columns: 1fr;  /* stack */
  }
  .composer__actions {
    justify-self: end;           /* keep button right-aligned under the box */
  }
}
</style>
<style>
/* Only active while SupportPage is mounted */
.route-support #app > nav { z-index: 1000; }  /* nav always above */
.route-support,
.route-support #app,
.route-support #app > main { background: #f7f9fc; } /* unify background */

/* Keep support hero layers behind nav */
.route-support .support .hero,
.route-support .support .hero__content { position: relative; z-index: 0; }
.route-support .support .hero__art { position: absolute; z-index: -1; }
</style>
